import api from "./index";

// ===== KPI / Monthly Goals =====

// Fetch all monthly goals for the authenticated user.
// Admins may pass { user_id } to view another agent's goals.
export const getMonthlyGoals = (params = {}) =>
  api.get("kpi-tracker/monthly-goals/", { params });

// Update a monthly goal (partial update; send only changed fields)
export const updateMonthlyGoal = (id, data) =>
  api.put(`kpi-tracker/monthly-goals/${id}/`, data);

// Create a monthly goal (current_* fields should be zero per requirement)
export const createMonthlyGoal = (data) =>
  api.post("kpi-tracker/monthly-goals/", data);

// Share KPI report via email for a specific monthly goal
export const shareMonthlyGoalReport = (id, email) =>
  api.post(`kpi-tracker/monthly-goals/${id}/generate-report/`, { email });

// download KPI report via email for a specific monthly goal
export const downloadMonthlyGoalReport = (id) =>
  api.post(`kpi-tracker/monthly-goals/${id}/download-report/`);


// ===== Commissions =====

// Fetch all commissions (with optional filters)
export const getCommissions = (params = {}) => 
  api.get("kpi-tracker/commissions/", { params });

// Get a single commission by ID
export const getCommission = (id) => 
  api.get(`kpi-tracker/commissions/${id}/`);

// Create a new commission
export const createCommission = (data) =>
  api.post("kpi-tracker/commissions/", data);

// Update a commission
export const updateCommission = (id, data) =>
  api.put(`kpi-tracker/commissions/${id}/`, data);

// Partially update a commission
export const patchCommission = (id, data) =>
  api.patch(`kpi-tracker/commissions/${id}/`, data);

// Delete a commission
export const deleteCommission = (id) =>
  api.delete(`kpi-tracker/commissions/${id}/`);

// Get commission leaderboard
export const getCommissionLeaderboard = (params = {}) =>
  api.get("kpi-tracker/commissions/leaderboard/", { params });

// Get sold property commissions (detailed list)
export const getSoldPropertyCommissions = (params = {}) =>
  api.get("kpi-tracker/commissions/sold-properties/", { params });

// Get the underlying rows behind a Leaderboard figure (dedicated stat pages).
// metric ∈ on_market | off_market | listings | appraisals | leads | lost | live
export const getLeaderboardBreakdown = (params = {}) =>
  api.get("kpi-tracker/commissions/leaderboard/breakdown/", { params });

// Get commission statistics for an agent
export const getCommissionStats = (params = {}) =>
  api.get("kpi-tracker/commissions/stats/", { params });


// ===== Agent Analytics (Analytics > Agents tab) =====

// Per-agent performance metrics for the selected period: current listings,
// listing win rate, listings lost, appraisals, appraisal→listing conversion,
// days on market, average commission, where listings come from, leads
// generated/won/lost, leads by source, lead→appraisal rate and lead→database
// rate. segment ∈ sales (default) | rentals.
export const getAgentAnalytics = (params = {}) =>
  api.get("kpi-tracker/agents/analytics/", { params });

// Rows behind one agent's metric card (click-through drill-down).
// metric ∈ current_listings | listings_won | on_market | off_market |
//          listings_lost | appraisals | appraisals_withdrawn |
//          leads_generated | leads_won | leads_lost | leads_to_database |
//          actioned_24h | aging_leads | commissions
// segment ∈ sales (default) | rentals.
export const getAgentAnalyticsDetail = (params = {}) =>
  api.get("kpi-tracker/agents/analytics/detail/", { params });


// ===== CRM Analytics (Analytics > CRM Analytics) =====
//
// Where the Agents tab above measures OUTCOMES (listings, appraisals,
// commission), these measure BEHAVIOUR: is the team actually using the CRM,
// and does that usage turn into revenue.

// Per-agent utilisation: active days, module usage, database coverage and the
// CRM Utilisation Score /100 with its explainable breakdown.
// Params: start_date, end_date, sort, include=matrix, include_non_sales, refresh
export const getCrmUsage = (params = {}) =>
  api.get("kpi-tracker/crm/usage/", { params });

// The agency cockpit: 13 headline cards + a utilisation leaderboard.
// Pass compare=1 for period-over-period deltas.
export const getCrmOverview = (params = {}) =>
  api.get("kpi-tracker/crm/overview/", { params });

// Rows behind any CRM Analytics number. Capped at 500 rows; the response
// carries `total` and `truncated`.
// metric ∈ crm_actions | active_agents | active_days | logins | contacts_touched |
//          calls | sms | email | database | pipeline | tasks | inspections |
//          appraisals | esign | campaigns | stage_changes | notes | prospecting |
//          buyer_activity | aging_leads | buyer_enquiries | listings_won |
//          pipeline_value | commissions
export const getCrmUsageDetail = (params = {}) =>
  api.get("kpi-tracker/crm/usage/detail/", { params });

// One CRM Analytics section. Every section returns the same shape
// ({cards, columns, rows, totals, breakdowns, notes, meta}), which is why a
// single panel component renders all of them.
// slug in prospecting | database | leads | buyers | inspections | tasks |
//         appraisals | listings | revenue | conversion | exec_overview |
//         exec_growth
// params: start_date, end_date, role, kind, source, market, refresh
export const getCrmSection = (slug, params = {}) =>
  api.get(`kpi-tracker/crm/section/${slug}/`, { params });

// Which sections this backend build serves, so a frontend that is ahead of the
// API degrades to hiding a tab rather than showing an error.
export const getCrmSections = () => api.get("kpi-tracker/crm/sections/");

// One agent's report card: their row in every section, plus an activity
// timeline. Agents may only request their own.
export const getCrmAgentProfile = (agentId, params = {}) =>
  api.get(`kpi-tracker/crm/agent/${agentId}/`, { params });


// ===== Dashboard aggregate stats =====

// Office-wide (admin) / own (agent) aggregate stats: GCI, pipeline lead counts,
// property counts (active/under-contract/sold). Powers the Agency Leaderboard cards.
export const getDashboardStats = (params = {}) =>
  api.get("kpi-tracker/dashboard/stats/", { params });


